import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  system: "Sistema",
  db: "Base de datos",
  security: "Seguridad",
  email: "Email",
  integrations: "Integraciones 3rd-party",
  performance: "Performance",
  ops: "Operaciones",
  config: "Configuración",
};

const SEVERITY_STYLES = {
  critical: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-900", icon: "✕", iconBg: "bg-rose-100 text-rose-700" },
  warning: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", icon: "!", iconBg: "bg-amber-100 text-amber-700" },
  info: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-900", icon: "i", iconBg: "bg-sky-100 text-sky-700" },
  ok: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: "✓", iconBg: "bg-emerald-100 text-emerald-700" },
};

export default function AdminInfraAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [filterSev, setFilterSev] = useState("");
  const [history, setHistory] = useState([]);
  const [autofixCatalog, setAutofixCatalog] = useState([]);
  const [fixingId, setFixingId] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get("/admin/infra-audit")
      .then(({ data: res }) => setData(res))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar auditoría"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/infra-audit")
      .then(({ data: res }) => { if (!cancelled) { setData(res); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    api.get("/admin/infra-audit/history?days=7")
      .then(({ data: res }) => { if (!cancelled) setHistory(res.items || []); })
      .catch(() => {});
    api.get("/admin/infra-audit/autofix-catalog")
      .then(({ data: res }) => { if (!cancelled) setAutofixCatalog(res.catalog || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const reloadHistory = () => {
    api.get("/admin/infra-audit/history?days=7")
      .then(({ data: res }) => setHistory(res.items || []))
      .catch(() => {});
  };

  const takeSnapshot = async () => {
    try {
      await api.post("/admin/infra-audit/snapshot");
      toast.success("Snapshot guardado");
      reloadHistory();
    } catch (e) {
      toast.error("No se pudo guardar el snapshot");
    }
  };

  const runAutoFix = async (checkId) => {
    setFixingId(checkId);
    try {
      const { data: res } = await api.post("/admin/infra-audit/auto-fix", { check_id: checkId });
      const diff = res.diff || {};
      const msgs = [];
      if (diff.delta_score != null && diff.delta_score !== 0) msgs.push(`Δ score: ${diff.delta_score > 0 ? "+" : ""}${diff.delta_score}`);
      if (diff.delta_db_mb != null && diff.delta_db_mb !== 0) msgs.push(`Δ DB: ${diff.delta_db_mb > 0 ? "+" : ""}${diff.delta_db_mb} MB`);
      toast.success(`Auto-fix: ${res.result?.action}${msgs.length ? ' · ' + msgs.join(' · ') : ''}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Auto-fix falló");
    } finally {
      setFixingId(null);
    }
  };

  const autofixIds = new Set(autofixCatalog.map((a) => a.id));

  if (!data) {
    return <div className="container-prose py-10" data-testid="admin-infra-audit-page">{loading ? "Ejecutando auditoría…" : "Sin datos"}</div>;
  }

  const filtered = data.checks.filter((c) => {
    if (filterCat && c.category !== filterCat) return false;
    if (filterSev && c.severity !== filterSev) return false;
    return true;
  });

  const categories = [...new Set(data.checks.map((c) => c.category))];
  const scoreColor = data.score >= 80 ? "text-emerald-700" : data.score >= 60 ? "text-amber-600" : "text-rose-600";

  return (
    <div className="container-prose py-10" data-testid="admin-infra-audit-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
          <h1 className="font-serif text-3xl text-gray-900 mt-2">Auditoría de infraestructura</h1>
          <p className="text-sm text-gray-600 mt-1">
            {data.checks.length} chequeos ejecutados en {data.elapsed_ms}ms · Generado: {new Date(data.generated_at).toLocaleString("es-CO")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={takeSnapshot} variant="outline" className="rounded-sm h-10" data-testid="snapshot-btn">
            Guardar snapshot
          </Button>
          <Button onClick={refresh} disabled={loading} className="rounded-sm h-10" data-testid="refresh-audit-btn">
            {loading ? "Ejecutando…" : "Re-ejecutar"}
          </Button>
        </div>
      </div>

      {/* SCORE + SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6" data-testid="audit-summary">
        <div className="border border-gray-300 bg-white p-5 text-center">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Score</p>
          <p className={`font-serif text-5xl font-semibold ${scoreColor}`} data-testid="score-value">{data.score}<span className="text-2xl text-gray-400">/100</span></p>
        </div>
        <SummaryCard label="OK" count={data.summary.ok || 0} tone="emerald" testid="sum-ok" />
        <SummaryCard label="Info" count={data.summary.info || 0} tone="sky" testid="sum-info" />
        <SummaryCard label="Warnings" count={data.summary.warning || 0} tone="amber" testid="sum-warning" />
        <SummaryCard label="Critical" count={data.summary.critical || 0} tone="rose" testid="sum-critical" />
      </div>

      {/* CLOUD RECOMMENDATIONS (mejoradas Iter25.h.2) */}
      {data.cloud_metrics && (
        <div className="border border-gray-200 bg-white p-4 mb-6" data-testid="cloud-recommendations">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg">Recursos cloud — consumo y recomendaciones</h2>
            <span className="text-[10px] uppercase tracking-widest text-gray-500">
              Pricing: {data.pricing?.currency || "USD"}/mes
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data.cloud_recommendations || []).map((r, i) => {
              const verdict = r.value?.verdict;
              const toneClass =
                r.severity === "critical" ? "border-rose-300 bg-rose-50" :
                r.severity === "warning" ? "border-amber-300 bg-amber-50" :
                r.severity === "info" ? "border-sky-200 bg-sky-50" :
                "border-emerald-200 bg-emerald-50";
              const verdictBadge =
                verdict === "over_provisioned" ? { label: "Sobre-provisionado", cls: "bg-sky-100 text-sky-800" } :
                verdict === "under_provisioned" ? { label: "Sub-provisionado", cls: "bg-rose-100 text-rose-800" } :
                verdict === "right_sized" ? { label: "Right-sized", cls: "bg-emerald-100 text-emerald-800" } :
                null;
              const cost = r.estimated_cost_usd_month || 0;
              return (
                <details key={i} className={`border ${toneClass} group`} data-testid={`cloud-rec-${i}`}>
                  <summary className="cursor-pointer list-none p-3 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900">{r.resource}</p>
                        {verdictBadge && (
                          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${verdictBadge.cls}`} data-testid={`cloud-rec-${i}-verdict`}>
                            {verdictBadge.label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-700">{r.current}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-mono font-semibold ${cost > 0 ? 'text-rose-700' : cost < 0 ? 'text-emerald-700' : 'text-gray-500'}`}>
                        {cost > 0 ? '+' : ''}${Math.abs(cost).toFixed(0)}/mo
                      </p>
                      <span className="text-[10px] text-gray-500">▼ ver detalles</span>
                    </div>
                  </summary>
                  <div className="px-3 pb-3 border-t border-white/50">
                    <p className="text-xs text-gray-800 mt-2 leading-relaxed">→ <strong>{r.suggested}</strong></p>
                    <div className="text-xs text-gray-700 mt-2 bg-white/60 border-l-2 border-amber-400 pl-2 py-1.5">
                      💡 {r.reason}
                    </div>
                    {r.value && (
                      <pre className="mt-2 text-[10px] font-mono bg-white/70 p-2 overflow-x-auto rounded-sm" data-testid={`cloud-rec-${i}-value`}>
                        {JSON.stringify(r.value, null, 2)}
                      </pre>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}

      {/* TRENDS (Iter25.h.2) — 6 mini-charts con toggle 24h/7d/30d */}
      <TrendsSection />

      {/* FORECAST (Iter25.h.2) — proyección 30/60/90 días */}
      <ForecastSection />


      {/* HISTORY (legacy chart removido — ahora reemplazado por <TrendsSection /> arriba) */}

      {/* SYSTEM SNAPSHOT */}
      <div className="border border-gray-200 bg-white p-4 mb-6" data-testid="system-snapshot">
        <h2 className="font-serif text-lg mb-3">Snapshot del sistema</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <SystemField label="Uptime" value={data.system.uptime_human} />
          <SystemField label="Python" value={data.system.python} mono />
          <SystemField label="CPU sistema" value={data.system.cpu_percent != null ? `${data.system.cpu_percent}% de ${data.system.cpu_count_logical} vCPU` : "—"} />
          <SystemField label="Load avg (1·5·15m)" value={data.system.load_15m != null ? `${data.system.load_1m} · ${data.system.load_5m} · ${data.system.load_15m}` : "—"} mono />
          <SystemField label="Memoria sistema" value={data.system.mem_percent != null ? `${data.system.mem_percent}% (${data.system.mem_used_gb}/${data.system.mem_total_gb} GB)` : "—"} />
          <SystemField label="Memoria proceso" value={data.system.process_rss_mb ? `${data.system.process_rss_mb} MB RSS` : "—"} />
          <SystemField label="Disco" value={data.system.disk_used_pct != null ? `${data.system.disk_used_pct}% (${data.system.disk_free_gb}/${data.system.disk_total_gb} GB libres)` : "—"} />
          <SystemField label="MongoDB" value={data.mongo?.ok ? `${data.mongo.data_size_mb} MB · ${data.mongo.objects?.toLocaleString()} docs` : "DESCONECTADO"} />
        </div>
        {data.mongo?.top_collections && data.mongo.top_collections.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Top colecciones por docs</p>
            <div className="flex flex-wrap gap-2">
              {data.mongo.top_collections.map((c) => (
                <span key={c.name} className="text-xs px-2 py-1 bg-gray-100 border border-gray-200 font-mono" data-testid={`top-coll-${c.name}`}>
                  {c.name}: <strong>{c.docs.toLocaleString()}</strong>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* FILTROS */}
      <div className="border border-gray-200 bg-white p-3 mb-4 flex flex-wrap gap-2" data-testid="audit-filters">
        <button onClick={() => { setFilterCat(""); setFilterSev(""); }} className={`px-3 h-8 text-xs border ${!filterCat && !filterSev ? 'bg-primary text-white border-primary' : 'bg-white border-gray-300 hover:bg-gray-50'}`} data-testid="filter-all">
          Todos ({data.checks.length})
        </button>
        <button onClick={() => setFilterSev("critical")} className={`px-3 h-8 text-xs border ${filterSev === "critical" ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-gray-300 hover:bg-rose-50'}`} data-testid="filter-critical">
          Critical ({data.summary.critical || 0})
        </button>
        <button onClick={() => setFilterSev("warning")} className={`px-3 h-8 text-xs border ${filterSev === "warning" ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-gray-300 hover:bg-amber-50'}`} data-testid="filter-warning">
          Warnings ({data.summary.warning || 0})
        </button>
        <button onClick={() => setFilterSev("info")} className={`px-3 h-8 text-xs border ${filterSev === "info" ? 'bg-sky-600 text-white border-sky-600' : 'bg-white border-gray-300 hover:bg-sky-50'}`} data-testid="filter-info">
          Info ({data.summary.info || 0})
        </button>
        <button onClick={() => setFilterSev("ok")} className={`px-3 h-8 text-xs border ${filterSev === "ok" ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-300 hover:bg-emerald-50'}`} data-testid="filter-ok">
          OK ({data.summary.ok || 0})
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCat(filterCat === cat ? "" : cat)}
            className={`px-3 h-8 text-xs border ${filterCat === cat ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-cat-${cat}`}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* CHECKLIST */}
      <div className="space-y-2" data-testid="checks-list">
        {filtered.length === 0 && (
          <div className="border border-gray-200 bg-white p-6 text-center text-gray-500">
            Sin chequeos en el filtro actual.
          </div>
        )}
        {filtered.map((c) => {
          const sty = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.info;
          return (
            <div
              key={c.id}
              className={`border ${sty.border} ${sty.bg} p-4 flex items-start gap-4`}
              data-testid={`check-${c.id}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full ${sty.iconBg} flex items-center justify-center font-bold text-sm`}>
                {sty.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <p className={`text-sm font-semibold ${sty.text}`}>{c.label}</p>
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">
                    {CATEGORY_LABELS[c.category] || c.category}
                  </span>
                </div>
                {c.detail && <p className="text-xs text-gray-700 mt-1" data-testid={`check-${c.id}-detail`}>{c.detail}</p>}
                {c.recommendation && (
                  <div className="mt-2 text-xs text-gray-800 bg-white/70 border-l-2 border-gray-400 pl-3 py-1.5" data-testid={`check-${c.id}-recommendation`}>
                    <strong className="text-[10px] uppercase tracking-widest text-gray-500 block mb-0.5">Recomendación</strong>
                    {c.recommendation}
                  </div>
                )}
                {autofixIds.has(c.id) && c.severity !== "ok" && (
                  <Button
                    size="sm"
                    onClick={() => runAutoFix(c.id)}
                    disabled={fixingId === c.id}
                    className="mt-3 rounded-sm h-8 text-xs bg-gray-900 hover:bg-gray-800"
                    data-testid={`autofix-${c.id}`}
                  >
                    {fixingId === c.id ? "Ejecutando…" : "⚡ Auto-fix"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, count, tone, testid }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    rose: "border-rose-300 bg-rose-50 text-rose-800",
  }[tone] || "border-gray-200 bg-gray-50 text-gray-800";
  return (
    <div className={`border p-4 text-center ${colors}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{label}</p>
      <p className="font-serif text-3xl">{count}</p>
    </div>
  );
}

function SystemField({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`${mono ? 'font-mono text-[11px]' : 'text-sm'} text-gray-900`}>{value ?? "—"}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TrendsSection — 6 mini-charts: CPU %, MEM %, DISK %, MongoDB MB, WARN, CRIT
// con toggle 24h / 7d / 30d + indicador de confianza.
// ─────────────────────────────────────────────────────────────────────────
function TrendsSection() {
  const [range, setRange] = useState("7d");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const days = range === "24h" ? 1 : range === "7d" ? 7 : 30;
    Promise.resolve().then(() => { if (alive) setLoading(true); });
    api.get(`/admin/infra-audit/history?days=${days}`)
      .then(({ data: res }) => { if (alive) setItems(res.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, reloadTick]);

  const triggerReload = useCallback(() => setReloadTick((t) => t + 1), []);

  const enough = items.length >= 5;
  const hoursLeft = enough ? 0 : Math.max(0, 5 - items.length);

  const chartData = items.map((h) => {
    const ss = h.system_snapshot || {};
    return {
      t: new Date(h.generated_at).toLocaleString("es-CO", { month: "short", day: "2-digit", hour: "2-digit" }),
      cpu: ss.cpu_percent ?? null,
      mem: ss.mem_percent ?? null,
      disk: ss.disk_used_pct ?? null,
      mongo: (h.mongo_snapshot?.data_size_mb || 0) / 1024,  // GB
      warn: h.summary?.warning || 0,
      crit: h.summary?.critical || 0,
    };
  });

  return (
    <div className="border border-gray-200 bg-white p-4 mb-6" data-testid="trends-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-serif text-lg">Tendencias del sistema</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {items.length} puntos · Snapshots horarios automáticos
            {!enough && (
              <span className="ml-2 text-amber-700 font-medium" data-testid="trends-insufficient">
                ⚠ Faltan ~{hoursLeft}h para tendencia confiable
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-1">
          {["24h", "7d", "30d"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 h-8 text-xs border ${range === r ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-300'}`}
              data-testid={`range-${r}`}
            >{r}</button>
          ))}
          <button
            onClick={triggerReload}
            disabled={loading}
            className="px-3 h-8 text-xs border border-gray-300 hover:bg-gray-50"
            data-testid="trends-refresh"
          >Actualizar</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MiniChart title="CPU %" data={chartData} dataKey="cpu" color="#7a2e2e" unit="%" />
        <MiniChart title="Memoria %" data={chartData} dataKey="mem" color="#a16e3a" unit="%" />
        <MiniChart title="Disco %" data={chartData} dataKey="disk" color="#5e7ac8" unit="%" />
        <MiniChart title="MongoDB (GB)" data={chartData} dataKey="mongo" color="#0c2461" unit="GB" decimals={3} />
        <MiniChart title="Items WARN" data={chartData} dataKey="warn" color="#d97706" />
        <MiniChart title="Items FAIL" data={chartData} dataKey="crit" color="#dc2626" />
      </div>
    </div>
  );
}

function MiniChart({ title, data, dataKey, color, unit = "", decimals = 1 }) {
  const last = data.length ? data[data.length - 1][dataKey] : null;
  return (
    <div className="border border-gray-100 bg-gray-50/40 p-3" data-testid={`mini-${dataKey}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-gray-500">{title}</span>
        <span className="text-sm font-mono font-semibold text-gray-900">
          {last == null ? "—" : `${Number(last).toFixed(decimals)}${unit}`}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={70}>
        <LineChart data={data}>
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} dot={false} />
          <XAxis dataKey="t" tick={false} axisLine={false} />
          <YAxis tick={false} axisLine={false} />
          <Tooltip formatter={(v) => `${Number(v).toFixed(decimals)}${unit}`} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ForecastSection — proyección de costos a 30/60/90 días.
// ─────────────────────────────────────────────────────────────────────────
function ForecastSection() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/infra-audit/forecast?days=${days}`)
      .then(({ data: res }) => { if (alive) setData(res); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [days]);

  if (!data) return null;
  const confidenceLabel = {
    insufficient_data: { label: "Sin datos suficientes", cls: "bg-gray-200 text-gray-700" },
    low: { label: "Confianza baja", cls: "bg-amber-100 text-amber-800" },
    medium: { label: "Confianza media", cls: "bg-sky-100 text-sky-800" },
    high: { label: "Confianza alta", cls: "bg-emerald-100 text-emerald-800" },
  }[data.confidence] || { label: data.confidence, cls: "bg-gray-200 text-gray-700" };

  return (
    <div className="border border-gray-200 bg-white p-4 mb-6" data-testid="forecast-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-serif text-lg">Forecast de costos</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Proyección por regresión lineal · {data.points} snapshots usados
            <span className={`ml-2 px-2 py-0.5 text-[10px] uppercase tracking-widest ${confidenceLabel.cls}`} data-testid="forecast-confidence">
              {confidenceLabel.label}
            </span>
          </p>
        </div>
        <div className="flex gap-1">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 h-8 text-xs border ${days === d ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-300'}`}
              data-testid={`forecast-${d}d`}
            >{d}d</button>
          ))}
        </div>
      </div>
      {data.confidence === "insufficient_data" ? (
        <p className="text-sm text-gray-600">
          Necesitamos al menos {data.min_points_recommended} snapshots horarios para proyectar (faltan {Math.max(0, data.min_points_recommended - data.points)}h).
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ForecastCard label="Disco proyectado" value={`${data.disk_projected_pct}%`} testid="forecast-disk" />
          <ForecastCard label="MongoDB proyectado" value={`${data.mongo_projected_gb} GB · ${data.mongo_projected_tier?.tier || '—'}`} testid="forecast-mongo" />
          <ForecastCard label={`Costo extra estimado (${data.days}d)`} value={`+$${data.projected_extra_cost_usd} USD`} testid="forecast-cost" tone={data.projected_extra_cost_usd > 0 ? "warn" : "ok"} />
        </div>
      )}
    </div>
  );
}

function ForecastCard({ label, value, testid, tone }) {
  const cls = tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-gray-200 bg-gray-50 text-gray-900";
  return (
    <div className={`border ${cls} p-3`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest opacity-70">{label}</p>
      <p className="text-lg font-serif mt-1">{value}</p>
    </div>
  );
}

